import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastStack, type ToastMsg } from '@/shared/ui';
import { ApiError } from '@/shared/api/client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, err) => !(err instanceof ApiError && [401, 403, 404, 409, 422, 429].includes(err.status)) && count < 2,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

type ToastApi = { notify: (t: Omit<ToastMsg, 'id'>) => void };
const ToastCtx = createContext<ToastApi>({ notify: () => undefined });
export const useToast = () => useContext(ToastCtx);

export function Providers({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const notify = useCallback((t: Omit<ToastMsg, 'id'>) => setToasts((prev) => [...prev.slice(-4), { ...t, id: Date.now() + Math.random() }]), []);
  const dismiss = useCallback((id: number) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const value = useMemo(() => ({ notify }), [notify]);
  return (
    <QueryClientProvider client={queryClient}>
      <ToastCtx.Provider value={value}>
        {children}
        <ToastStack toasts={toasts} onDismiss={dismiss} />
      </ToastCtx.Provider>
    </QueryClientProvider>
  );
}
